import axios from 'axios';

export type DisasterType = 'earthquake' | 'flood' | 'fire' | 'cyclone' | 'volcano' | 'tsunami' | 'drought' | 'other';

export interface DisasterEvent {
  id: string;
  type: DisasterType;
  title: string;
  description: string;
  location: {
    name: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
  severity: 'critical' | 'high' | 'medium' | 'low';
  magnitude?: number;
  timestamp: string;
  sources: string[];
}

class DisasterService {
  private static instance: DisasterService;

  static getInstance(): DisasterService {
    if (!DisasterService.instance) {
      DisasterService.instance = new DisasterService();
    }
    return DisasterService.instance;
  }

  // ==================== USGS EARTHQUAKES ====================
  async getEarthquakes(): Promise<DisasterEvent[]> {
    try {
      console.log('🌋 Fetching earthquakes...');
      const response = await axios.get('https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/2.5_day.geojson');
      
      return response.data.features.slice(0, 30).map((eq: any) => ({
        id: `eq-${eq.id}`,
        type: 'earthquake',
        title: `M${eq.properties.mag.toFixed(1)} - ${eq.properties.place}`,
        description: `Depth: ${eq.geometry.coordinates[2].toFixed(1)}km • ${new Date(eq.properties.time).toLocaleString()}`,
        location: {
          name: eq.properties.place,
          coordinates: {
            lat: eq.geometry.coordinates[1],
            lng: eq.geometry.coordinates[0]
          }
        },
        severity: eq.properties.mag >= 6 ? 'critical' : eq.properties.mag >= 5 ? 'high' : 'medium',
        magnitude: eq.properties.mag,
        timestamp: new Date(eq.properties.time).toISOString(),
        sources: ['USGS']
      }));
    } catch (error) {
      console.error('Error fetching earthquakes:', error);
      return [];
    }
  }

  // ==================== NASA EONET FIRES ====================
  async getFires(): Promise<DisasterEvent[]> {
    try {
      console.log('🔥 Fetching fires...');
      const response = await axios.get('https://eonet.gsfc.nasa.gov/api/v3/events', {
        params: {
          category: 'wildfires',
          status: 'open',
          limit: 30
        }
      });
      
      return response.data.events.map((event: any, index: number) => {
        let lat = 0, lng = 0;
        if (event.geometry?.[0]?.coordinates) {
          lng = event.geometry[0].coordinates[0];
          lat = event.geometry[0].coordinates[1];
        }
        
        return {
          id: `fire-${event.id || index}`,
          type: 'fire',
          title: event.title || 'Wildfire',
          description: event.description || 'Active wildfire detected',
          location: {
            name: event.title?.split(' at ')[1] || 'Unknown',
            coordinates: { lat, lng }
          },
          severity: 'high',
          timestamp: new Date(event.geometry?.[0]?.date || Date.now()).toISOString(),
          sources: ['NASA EONET']
        };
      });
    } catch (error) {
      console.error('Error fetching fires:', error);
      return [];
    }
  }

  // ==================== GDACS DISASTERS ====================
  async getGDACS(): Promise<DisasterEvent[]> {
    try {
      console.log('🌍 Fetching global disasters...');
      const response = await axios.get(
        'https://www.gdacs.org/gdacsapi/api/events/geteventlist/SEARCH?eventtypes=FL,TC,VO,TS,DR'
      );
      
      if (!response.data?.features) return [];
      
      return response.data.features.slice(0, 30).map((f: any) => {
        const props = f.properties;
        const coords = f.geometry?.coordinates || [0, 0];
        
        let type: DisasterType = 'other';
        let severity: 'critical' | 'high' | 'medium' | 'low' = 'medium';
        
        if (props.eventtype === 'FL') {
          type = 'flood';
          severity = props.alertlevel === 'Red' ? 'critical' : 'high';
        } else if (props.eventtype === 'TC') {
          type = 'cyclone';
          severity = props.alertlevel === 'Red' ? 'critical' : 'high';
        } else if (props.eventtype === 'VO') {
          type = 'volcano';
          severity = props.alertlevel === 'Red' ? 'critical' : 'high';
        } else if (props.eventtype === 'TS') {
          type = 'tsunami';
          severity = 'critical';
        } else if (props.eventtype === 'DR') {
          type = 'drought';
          severity = 'medium';
        }

        return {
          id: `gd-${props.eventid}`,
          type,
          title: props.eventname || `${type} in ${props.country || 'unknown'}`,
          description: props.description || `${type} event`,
          location: {
            name: props.country || 'Unknown',
            coordinates: { lat: coords[1] || 0, lng: coords[0] || 0 }
          },
          severity,
          magnitude: props.magnitude,
          timestamp: new Date(props.fromdate).toISOString(),
          sources: ['GDACS']
        };
      });
    } catch (error) {
      console.error('Error fetching GDACS:', error);
      return [];
    }
  }

  // ==================== GET ALL DISASTERS ====================
  async getAllDisasters(): Promise<DisasterEvent[]> {
    try {
      const [earthquakes, fires, gdacs] = await Promise.all([
        this.getEarthquakes(),
        this.getFires(),
        this.getGDACS()
      ]);

      const all = [...earthquakes, ...fires, ...gdacs];
      
      // Remove duplicates
      const unique = Array.from(new Map(all.map(item => [item.id, item])).values());
      
      // Sort by date (newest first)
      return unique.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
    } catch (error) {
      console.error('Error fetching all disasters:', error);
      return [];
    }
  }
}

export default DisasterService.getInstance();